import { defineStore } from 'pinia'
import { ref, watch, onScopeDispose } from 'vue'
import { WorkflowStage, Template, WorkflowStageInfo, TemplateStatus } from '../types'
import { templateApi } from '../services/api'
import { initializeWorkflowStages, normalizeWorkflowStages, completeStage, skipStage, getNextStage } from '../utils/workflow'
import { loadRuntimeConfig, RuntimeConfig } from '../config/runtime'
import router from '../router'
import { parseTemplateId, ensureLocalCopy } from '../services/templateCache'
import { pdfjsLib } from '../utils/pdf'
import { generateDnaImage } from '../utils/dnaImage'

export const useWorkflowStore = defineStore('workflow', () => {
  // ── State ──────────────────────────────────────────────────
  const mode = ref<'dashboard' | 'workflow'>('dashboard')
  const currentTemplate = ref<Template | null>(null)
  const currentStage = ref<WorkflowStage>(WorkflowStage.UPLOAD)
  const workflowStages = ref<WorkflowStageInfo[]>(initializeWorkflowStages())
  const selectedStampz = ref<any | null>(null)
  const loadedTemplateId = ref<string | null>(null)
  const runtimeConfig = ref<RuntimeConfig | null>(null)
  const readOnlyTemplates = ref<Set<string>>(new Set())
  const isLoadingTemplate = ref(false)
  const loadingMessage = ref<string | null>(null)
  const loadError = ref<string | null>(null)
  const browseFolderId = ref<string | null>(null)

  // ── Init ───────────────────────────────────────────────────
  async function initConfig() {
    runtimeConfig.value = await loadRuntimeConfig()
  }

  // ── URL helpers ────────────────────────────────────────────
  function updateURL(
    newMode: 'dashboard' | 'workflow',
    template?: Template | null,
    stage?: WorkflowStage,
    push = false,
  ) {
    const nav = push ? router.push : router.replace
    if (newMode === 'dashboard') {
      nav.call(router, '/')
    }
    else if (newMode === 'workflow') {
      if (template && stage) {
        nav.call(router, `/${template.id}/${stage}`)
      }
      else if (stage) {
        nav.call(router, `/${stage}`)
      }
    }
  }

  // ── Template loading (from hash) ──────────────────────────
  async function loadTemplateFromHash(templateId: string, stage?: WorkflowStage) {
    if (loadedTemplateId.value === templateId) return
    loadedTemplateId.value = templateId // set immediately to prevent duplicate calls

    isLoadingTemplate.value = true
    loadError.value = null

    try {
      const parsed = parseTemplateId(templateId, runtimeConfig.value?.storageMode)
      let localTemplateId = templateId

      if (parsed && parsed.sourceType === 'gdrive') {
        try {
          const resolveResp = await fetch(`/api/gdrive/resolve/${parsed.remoteId}`)
          if (resolveResp.ok) {
            const resolved = await resolveResp.json()
            if (resolved.type === 'folder') {
              browseFolderId.value = resolved.id
              mode.value = 'dashboard'
              isLoadingTemplate.value = false
              loadingMessage.value = null
              return
            }
          }
        }
        catch (resolveError) {
          console.warn('Failed to resolve Drive ID, proceeding as file:', resolveError)
        }
      }

      mode.value = 'workflow'

      if (parsed) {
        console.log('Remote template detected, ensuring local copy:', templateId)
        try {
          const cachedTemplate = await ensureLocalCopy(templateId, parsed.sourceType, (msg) => {
            loadingMessage.value = msg
          })
          localTemplateId = cachedTemplate.id
          console.log('Using local copy:', localTemplateId)
        }
        catch (cacheError) {
          const detail = cacheError instanceof Error ? cacheError.message : String(cacheError)
          throw new Error(`Could not download remote template: ${detail}`)
        }
      }

      loadingMessage.value = 'Loading PDF...'

      const template = await templateApi.getTemplate(localTemplateId)
      if (template) {
        const pdfResponse = await fetch(`/api/templates/${localTemplateId}/pdf`)
        if (pdfResponse.ok) {
          const pdfBlob = await pdfResponse.blob()
          const pdfData = await pdfBlob.arrayBuffer()

          const loadingTask = pdfjsLib.getDocument({ data: pdfData.slice(0) })
          const pdfDoc = await loadingTask.promise
          const firstPage = await pdfDoc.getPage(1)
          const viewport = firstPage.getViewport({ scale: 1.0 })

          // Generate DNA image if not already stored in metadata
          let dnaImage = template.dnaImage || null
          if (!dnaImage) {
            dnaImage = await generateDnaImage(pdfDoc)
            if (dnaImage) {
              // Persist to zpaper/template metadata (fire and forget)
              fetch(`/api/templates/${localTemplateId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ template: { dnaImage } }),
              }).catch(() => {})
            }
          }

          const fullTemplate: Template = {
            id: template.id,
            clientName: template.clientName || template.fileName?.replace('.pdf', '') || 'Unknown',
            programName: template.programName || 'Program',
            fileName: template.fileName || `${localTemplateId}.pdf`,
            fileSize: template.fileSize || pdfBlob.size,
            pageCount: pdfDoc.numPages,
            uploadDate: new Date(template.uploadDate || Date.now()),
            lastModified: new Date(template.lastModified || Date.now()),
            status: template.status || TemplateStatus.UPLOADED,
            pdfData: pdfData,
            dnaImage: dnaImage || undefined,
            pdfinfo: template.pdfinfo || {
              fileSize: pdfBlob.size,
              pageCount: pdfDoc.numPages,
              pdfVersion: (pdfDoc as any).pdfInfo?.PDFFormatVersion || '1.7',
              hasAcroFields: false,
              isEncrypted: false,
              producer: '',
              creationDate: new Date().toISOString(),
              uploadDate: new Date().toISOString(),
              lastModified: new Date().toISOString(),
              pageWidth: viewport.width,
              pageHeight: viewport.height,
              orientation: viewport.width > viewport.height ? 'landscape' : 'portrait',
            },
            stampzConfig: template.stampzConfig,
            shlinkConfig: template.shlinkConfig,
            stickers: template.stickers || [],
            fields: template.fields || [],
            currentStage: template.currentStage || stage,
            workflowStages: normalizeWorkflowStages(template.workflowStages || initializeWorkflowStages()),
            createdBy: template.createdBy || 'unknown',
            assignedTo: template.assignedTo || 'srickus',
          }

          currentTemplate.value = fullTemplate
          const resolvedStage = stage || fullTemplate.currentStage || WorkflowStage.UPLOAD
          currentStage.value = resolvedStage
          workflowStages.value = fullTemplate.workflowStages
          mode.value = 'workflow'
          isLoadingTemplate.value = false
          loadingMessage.value = null
          return
        }
      }

      console.error('Template not found:', templateId)
      loadError.value = `Template not found: "${templateId}" does not exist or could not be loaded.`
      loadedTemplateId.value = null
      router.replace('/')
      mode.value = 'dashboard'
      isLoadingTemplate.value = false
      loadingMessage.value = null
    }
    catch (error) {
      console.error('Failed to load template from URL:', error)
      loadError.value =
        error instanceof Error ? error.message : `Failed to load template: ${String(error)}`
      loadedTemplateId.value = null
      router.replace('/')
      mode.value = 'dashboard'
      isLoadingTemplate.value = false
      loadingMessage.value = null
    }
  }

  // ── Actions ────────────────────────────────────────────────
  function selectTemplate(template: Template) {
    currentTemplate.value = template
    currentStage.value = template.currentStage
    workflowStages.value = normalizeWorkflowStages(template.workflowStages || initializeWorkflowStages())
    loadedTemplateId.value = template.id
    mode.value = 'workflow'
    updateURL('workflow', template, template.currentStage, true)
  }

  function newTemplate() {
    currentTemplate.value = null
    currentStage.value = WorkflowStage.UPLOAD
    workflowStages.value = initializeWorkflowStages()
    selectedStampz.value = null
    mode.value = 'workflow'
    updateURL('workflow', null, WorkflowStage.UPLOAD, true)
  }

  async function stageComplete(stage: WorkflowStage) {
    const updated = completeStage(workflowStages.value, stage)
    workflowStages.value = updated

    const nextStage = getNextStage(stage)
    if (nextStage) {
      currentStage.value = nextStage
      updateURL('workflow', currentTemplate.value, nextStage)
    }

    if (currentTemplate.value) {
      try {
        const response = (await templateApi.updateTemplateProgress(
          currentTemplate.value.id,
          nextStage || stage,
          updated,
        )) as any
        if (response.success && currentTemplate.value) {
          currentTemplate.value = {
            ...currentTemplate.value,
            currentStage: response.currentStage || nextStage || stage,
            workflowStages: normalizeWorkflowStages(response.workflowStages || updated),
          }
        }
      }
      catch (error) {
        console.error('Failed to update template progress:', error)
      }
    }
  }

  async function stageSelectNav(stage: WorkflowStage) {
    if (currentTemplate.value?.id) {
      try {
        const freshTemplate = await templateApi.getTemplate(currentTemplate.value.id)
        if (freshTemplate?.id) {
          currentTemplate.value = freshTemplate
          workflowStages.value = normalizeWorkflowStages(freshTemplate.workflowStages || workflowStages.value)
        }
      }
      catch (error) {
        console.error('Failed to reload template:', error)
      }
    }
    currentStage.value = stage
    updateURL('workflow', currentTemplate.value, stage)
  }

  async function stageSkipAction(stage: WorkflowStage) {
    const updated = skipStage(workflowStages.value, stage)
    workflowStages.value = updated

    const nextStage = getNextStage(stage)
    if (nextStage) {
      currentStage.value = nextStage
      updateURL('workflow', currentTemplate.value, nextStage)
    }

    if (currentTemplate.value?.id) {
      try {
        const response = (await templateApi.updateTemplateProgress(
          currentTemplate.value.id,
          nextStage || stage,
          updated,
        )) as any
        if (response.success && currentTemplate.value) {
          currentTemplate.value = {
            ...currentTemplate.value,
            currentStage: response.currentStage || nextStage || stage,
            workflowStages: normalizeWorkflowStages(response.workflowStages || updated),
          }
        }
      }
      catch (error) {
        console.error('Failed to update skipped stages:', error)
      }
    }
  }

  function stageAdvance(stage: WorkflowStage) {
    const nextStage = getNextStage(stage)
    if (nextStage) {
      currentStage.value = nextStage
      updateURL('workflow', currentTemplate.value, nextStage)
    }
  }

  async function saveDraft() {
    if (currentTemplate.value) {
      try {
        await templateApi.updateTemplateProgress(
          currentTemplate.value.id,
          currentStage.value,
          workflowStages.value,
        )
      }
      catch (error) {
        console.error('Failed to save draft:', error)
      }
    }
  }

  function backToDashboard() {
    mode.value = 'dashboard'
    currentTemplate.value = null
    loadedTemplateId.value = null
  }

  function templateUpdate(updated: Template) {
    currentTemplate.value = updated
  }

  // Mutates pdfData in place rather than replacing currentTemplate, so
  // watchers keyed on template identity (e.g. FhirMappingPage's mapping-
  // state hydration) don't re-fire and discard unsaved local edits.
  function updateTemplatePdfData(pdfData: ArrayBuffer) {
    if (currentTemplate.value) {
      currentTemplate.value.pdfData = pdfData
    }
  }

  function templateCreated(created: Template) {
    currentTemplate.value = created
    workflowStages.value = normalizeWorkflowStages(created.workflowStages || initializeWorkflowStages())
    updateURL('workflow', created, currentStage.value)
  }

  function clearLoadError() {
    loadError.value = null
  }

  // ── Handle browser back/forward navigation ────────────────
  const unsubRouterGuard = router.afterEach((to) => {
    if (to.name === 'dashboard' && mode.value !== 'dashboard') {
      mode.value = 'dashboard'
      currentTemplate.value = null
      loadedTemplateId.value = null
    }
  })
  onScopeDispose(unsubRouterGuard)

  // ── Auto-save watcher ─────────────────────────────────────
  let saveTimer: ReturnType<typeof setTimeout> | null = null
  watch(
    [() => currentTemplate.value?.id, currentStage, workflowStages],
    () => {
      if (saveTimer) clearTimeout(saveTimer)
      if (!currentTemplate.value?.id || workflowStages.value.length === 0) return
      if (readOnlyTemplates.value.has(currentTemplate.value.id)) return

      const id = currentTemplate.value.id
      saveTimer = setTimeout(async () => {
        try {
          await templateApi.updateTemplateProgress(id, currentStage.value, workflowStages.value)
        }
        catch {
          console.warn('Auto-save failed (template may be read-only):', id)
          readOnlyTemplates.value = new Set(readOnlyTemplates.value).add(id)
        }
      }, 2000)
    },
    { deep: true },
  )

  return {
    // state
    mode,
    currentTemplate,
    currentStage,
    workflowStages,
    selectedStampz,
    loadedTemplateId,
    runtimeConfig,
    isLoadingTemplate,
    loadingMessage,
    loadError,
    browseFolderId,
    // actions
    initConfig,
    loadTemplateFromHash,
    selectTemplate,
    newTemplate,
    stageComplete,
    stageSelectNav,
    stageSkipAction,
    stageAdvance,
    saveDraft,
    backToDashboard,
    templateUpdate,
    updateTemplatePdfData,
    templateCreated,
    clearLoadError,
    updateURL,
  }
})
